import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from '../../utilities/axios';
import * as API_END_POINT from '../../utilities/constant';
import './Brand.css'
import { useSearchParams, useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import SideFilter from '../../components/Common/SideFilter';
import FilterPills from '../../components/Common/Dropdowns/FilterPills';
import { Browse, BrandCard, BrandCardSkeleton, Pager } from '../../shared/components';
import { useStore } from '../../context/StoreContext';
import { useStorefrontTheme } from '../../shared/theme/storefrontTheme';
import {
  autocomplete,
  isSearchEnabled,
  searchBrands,
  buildSuggestionLabelLookup,
  formatSuggestionDisplayText,
  getSuggestionAheadText,
  getSuggestionTabValue,
  getSuggestionCompletedValue,
} from '../../services/searchApi';

/* BRANDS — every brand this store carries.

   It is `Browse` with brands as the item, not a second browse page. SP-main's
   `shop-brands.html` says the same of itself: "Same rail, same chips, same price
   bands, same drawer — configured, never copied. If this page needs a filter
   behaviour Shop All does not have, that is a change to the shared engine, not a
   fork here." The only addition the engine needed was `gridClass`, because a
   brand tile is 178px against a product's 185px.

   The four filters are the reference's list, and the brand filters endpoint
   returns exactly them: Category · Brand values · Price range · Animal type.
   `SideFilter` draws no Brand group here, correctly, because this page IS the
   brands.

   The search/list branching, the Meilisearch autocomplete, the filter builder
   and the URL sync are untouched: this was a change of presentation. */

// Reusable Dropdown Component

/* Copied verbatim from `StoreBuilder/brandsMarkup.js`, so a brand wears the same
   ring colour here as in the Home page's Featured Brands rail. It is a HASH of
   the name, not randomness: the same brand always lands on the same colour and
   two brands never swap when the list re-sorts. Keep the two in sync — a shared
   helper would be the better home. */
const BRAND_COLORS = [
  '#2b5137', '#466a85', '#8a5035', '#5a4f7c', '#755f38',
  '#3d635a', '#6f4859', '#4f5e3b', '#7a4242', '#3a5f6e',
];
const brandColorOf = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return BRAND_COLORS[Math.abs(hash) % BRAND_COLORS.length];
};

/* No "Most popular" — nothing behind it can sort by popularity (see SORTS
   below). The page opens A–Z. */
// The reference's brand sorts. Recommended = the seller's featured brands first, then A–Z.
const DEFAULT_SORT = 'Recommended';
const BRAND_SORT_FIELDS = {
  Recommended: 'store_name:asc',
  'Brand (A–Z)': 'store_name:asc',
  'Brand (Z–A)': 'store_name:desc',
  'Products (high to low)': 'product_count:desc',
  'Products (low to high)': 'product_count:asc',
};

const buildBrandFilterArr = (filters) => {
  const filterArr = [];

  if (filters.brandValues?.length > 0) {
    filterArr.push({
      field: 'brand_values',
      options: { operator: 'in', values: filters.brandValues.map(String) },
    });
  }

  if (filters.categories?.length > 0) {
    const subcategory = [];
    filters.categories.forEach((category) => {
      if (category?.subcategory?.length > 0) {
        subcategory.push(...category.subcategory);
      }
    });
    filterArr.push({
      field: 'subcategory',
      options: { operator: 'in', values: subcategory.map(String) },
    });
  }

  if (filters.tags?.length > 0) {
    filterArr.push({
      field: 'tags',
      options: { operator: 'in', values: filters.tags.map((e) => e === 'small_pet' ? 'small_animal' : e.toString()) },
    });
  }

  if (filters.sample?.length > 0) {
    filters.sample.forEach((val) => {
      filterArr.push({
        field: 'hassample',
        options: { value: val },
      });
    });
  }

  if (filters.priceRange?.length > 0) {
    const minValues = filters.priceRange
      .map((r) => r.min)
      .filter((v) => v !== undefined && v !== null);
    const maxValues = filters.priceRange
      .map((r) => r.max)
      .filter((v) => v !== undefined && v !== null && v !== Infinity);

    const option = {};
    if (minValues.length > 0) option.min = Math.min(...minValues);
    if (maxValues.length > 0) option.max = Math.max(...maxValues);

    if (Object.keys(option).length > 0) {
      filterArr.push({
        field: 'pricerange',
        options: option,
      });
    }
  }

  return filterArr;
};

const getBrandMeiliSort = (sortBy) => BRAND_SORT_FIELDS[sortBy] || BRAND_SORT_FIELDS[DEFAULT_SORT];

const getMatchedHighlightText = (hit) => {
  const matchedData = hit?.matchedData || {};
  const columns = String(hit?.matchedColumns || '')
    .split(',')
    .map((col) => col.trim())
    .filter(Boolean);

  for (const column of columns) {
    const value = matchedData[column];
    if (value) return value;
  }

  return (
    matchedData.brand_name ||
    matchedData.title ||
    hit?._formatted?.brand_name ||
    hit?._formatted?.title ||
    hit?.brand_name ||
    hit?.title ||
    ''
  );
};

/* The old grid `Loader` went with the grid it filled — `BrandCardSkeleton`
   draws the real tile's shape instead. */
const Brands = () => {
  const { pageDetails } = useStore();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 10 });
  const [filtersView, setfiltersView] = useState([]);
  const [filters, setFilters] = useState({
    categories: [],
    tags: [],
    discounts: [],
    priceRange: [],
    brandValues: [],
    sample: [],
  });
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [brandsData, setBrandsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit] = useState(100);
 
  const queryKey = `${JSON.stringify(filters)}|${sortBy}|${searchTerm}`;
  const [pageOf, setPageOf] = useState({ key: queryKey, page: 1 });
  const currentPage = pageOf.key === queryKey ? pageOf.page : 1;
  const offset = (currentPage - 1) * limit;
  const handlePageChange = (page) => {
    setPageOf({ key: queryKey, page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [rawSearch, setRawSearch] = useState('');
  const [ghostText, setGhostText] = useState('');
  const [BrandFilters, setBrandFilters] = useState(null);
  const [manualPriceRange, setManualPriceRange] = useState({ min: '', max: '' });
  const suggestionFullTextRef = useRef('');

  const suggestionLabelLookup = useMemo(
    () => buildSuggestionLabelLookup(BrandFilters),
    [BrandFilters]
  );

  const autocompleteCancelRef = useRef(null);
  const latestAutocompleteReqRef = useRef(0);
  const searchTermRef = useRef(searchTerm);
  const rawSearchRef = useRef(rawSearch);
  searchTermRef.current = searchTerm;
  rawSearchRef.current = rawSearch;

  const cancelInFlightAutocomplete = useCallback(() => {
    latestAutocompleteReqRef.current += 1;
    if (autocompleteCancelRef.current) {
      autocompleteCancelRef.current.cancel('Cancelled due to new autocomplete request');
      autocompleteCancelRef.current = null;
    }
  }, []);

  const debouncedSetSearch = useCallback(
    debounce((val) => {
      if (val !== searchTermRef.current) setSearchTerm(val);
    }, 500),
    []
  );

  const runAutocomplete = useCallback(async (val, currentFilters, currentSortBy) => {
    if (!val || !isSearchEnabled() || /\s$/.test(val)) {
      cancelInFlightAutocomplete();
      suggestionFullTextRef.current = '';
      setGhostText('');
      return;
    }

    cancelInFlightAutocomplete();
    const requestId = latestAutocompleteReqRef.current;
    const source = axios.CancelToken.source();
    autocompleteCancelRef.current = source;

    try {
      const result = await autocomplete(val, {
        limit: 1,
        offset: 0,
        filter: buildBrandFilterArr(currentFilters),
        sort: getBrandMeiliSort(currentSortBy),
        index: 'brands',
        cancelToken: source.token,
      });

      if (requestId !== latestAutocompleteReqRef.current) return;
      if (val !== rawSearchRef.current) return;
      if (searchInputRef.current && document.activeElement !== searchInputRef.current) return;

      let suggestion = '';
      const hits = result?.data?.hits || result?.hits || [];
      if (Array.isArray(hits) && hits.length > 0) {
        const highlightedText = getMatchedHighlightText(hits[0]);
        const displayText = formatSuggestionDisplayText(highlightedText, suggestionLabelLookup);
        suggestion = getSuggestionAheadText(
          val,
          highlightedText,
          displayText,
          suggestionLabelLookup
        );
        suggestionFullTextRef.current = getSuggestionTabValue(
          val,
          highlightedText,
          displayText,
          suggestion,
          suggestionLabelLookup
        );
      } else {
        suggestionFullTextRef.current = '';
      }
      setGhostText(suggestion);
    } catch (error) {
      if (axios.isCancel(error)) return;
      if (requestId !== latestAutocompleteReqRef.current) return;
      suggestionFullTextRef.current = '';
      setGhostText('');
    }
  }, [cancelInFlightAutocomplete, suggestionLabelLookup]);

  const debouncedAutocomplete = useCallback(
    debounce(runAutocomplete, 200, { maxWait: 600 }),
    [runAutocomplete]
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
      debouncedAutocomplete.cancel();
      cancelInFlightAutocomplete();
    };
  }, [debouncedSetSearch, debouncedAutocomplete, cancelInFlightAutocomplete]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Tab' && (ghostText || suggestionFullTextRef.current)) {
      e.preventDefault();
      debouncedSetSearch.cancel();
      debouncedAutocomplete.cancel();
      cancelInFlightAutocomplete();
      const newValue = getSuggestionCompletedValue(
        rawSearch,
        ghostText,
        suggestionFullTextRef.current
      );
      setRawSearch(newValue);
      setSearchTerm(newValue);
      suggestionFullTextRef.current = '';
      setGhostText('');
    } else if (e.key === 'Escape') {
      suggestionFullTextRef.current = '';
      setGhostText('');
    }
  };

  const cancelReq = useRef(null);
  const latestReqRef = useRef(0);
  const fetchBrandDetails = useCallback(async () => {
    setIsLoading(true);
    latestReqRef.current += 1;
    const requestId = latestReqRef.current;

    // Cancel previous request if it exists
    if (cancelReq?.current) {
        cancelReq?.current?.cancel("Cancelled due to new request"); // now works
    }

    // Create new cancel token SOURCE (IMPORTANT)
    const source = axios.CancelToken.source();
    cancelReq.current = source;
    try {
      const filterArr = buildBrandFilterArr(filters);
      const sort = getBrandMeiliSort(sortBy);

      const response = await searchBrands(searchTerm.trim(), {
        filter: filterArr,
        sort,
        useFeaturedSort: sortBy === 'Recommended',
        limit,
        offset,
        cancelToken: source.token,
      });

      // Ignore outdated responses
      if (requestId !== latestReqRef.current) return;

      const items = response?.data?.items || response?.items || response?.data?.hits || response?.hits || [];
      const pagination = response?.data?.pagination || null;
      const totalItems = pagination?.totalItems ?? response?.data?.estimatedTotalHits ?? response?.estimatedTotalHits ?? items.length;

      setBrandsData(Array.isArray(items) ? items : []);
      setPagination((prev) => ({
        ...prev,
        currentPage: pagination?.currentPage ?? currentPage,
        totalItems,
        itemsPerPage: pagination?.itemsPerPage ?? limit,
        totalPages: pagination?.totalPages ?? Math.max(1, Math.ceil(totalItems / limit)),
      }));
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      // Only the latest successful request controls loading
      if (requestId === latestReqRef.current) {
          setTimeout(()=>{
            setIsLoading(false);
          },1500)
      }
    }
  }, [filters, sortBy, currentPage, searchTerm, limit, offset]);
  const firstRender = useRef(true);

  useEffect(() => {
    // if (firstRender.current) {
    //   firstRender.current = false;
    //   return;
    // }
    fetchBrandDetails();
  }, [fetchBrandDetails]);

  useEffect(() => {
    // fetchBrandDetails();
    getAllBrandsFilter();
  }, [])
  const getAllBrandsFilter = async () => {
    const response = await axios.post(
      `${API_END_POINT.BRAND_FILTERS}`,
      {}
    );
    setBrandFilters({ ...response.data.data?.availableFilters })
  }
  const clearFilters = () => {
    setManualPriceRange({min: '', max: ''})
    setFilters({
      categories: [],
      tags: [],
      discounts: [],
      priceRange: [],
      brandValues: [],
      sample: [],
    });
    setSearchParams({});
  };
  /* The partner owns the skin, same as /all-products: `.sfx` resolves the static
     tokens, but --o and --m come from the saved Style config and have to be
     written onto the element.
     ⛔ No `font-family-inter` on the wrapper — it is `!important` and overrode
     the design system's General Sans; `.sfx` declares the typeface. */
  const sfxRef = useStorefrontTheme(pageDetails?.page_data?.colorAndApperances);

  /* Every sort is a field the API will actually order by. */
  const sortOptions = Object.keys(BRAND_SORT_FIELDS);

  /* The grid waits for the facets as well as the brands: a full grid beside an
     empty rail reads as a store with no filters. */
  const isBusy = isLoading || !BrandFilters;

  const appliedCount = filtersView?.length || 0;

  return (
    <div ref={sfxRef} className="sfx">
      {/* No PageLayout: it wraps children in `max-w-7xl mx-auto` and renders its
          own breadcrumb and heading, while `.br-wrap` is 1240px with a 40px
          gutter and owns both. */}
      <Browse
        title="Brands"
        crumb={[{ label: 'Home', href: '/' }, { label: 'Brands' }]}
        intro="The brands behind what we recommend — every one of them chosen, not advertised to us."
        noun="brands"
        gridClass="br-bgrid"
        total={pagination?.totalItems}
        items={isBusy ? [] : brandsData}
        loading={isBusy}
        loadingView={[...Array(8)].map((_, i) => <BrandCardSkeleton key={i} />)}
        searchRef={searchInputRef}
        searchValue={rawSearch}
        searchPlaceholder="Search brands"
        onSearchChange={(val) => {
          setRawSearch(val);
          debouncedSetSearch(val);
          if (val && !/\s$/.test(val)) {
            debouncedAutocomplete(val, filters, sortBy);
          } else {
            debouncedAutocomplete.cancel();
            cancelInFlightAutocomplete();
            suggestionFullTextRef.current = '';
            setGhostText('');
          }
        }}
        onSearchKeyDown={handleSearchKeyDown}
        onSearchBlur={() => {
          suggestionFullTextRef.current = '';
          setGhostText('');
        }}
        sorts={sortOptions.map((o) => [o, o])}
        sortValue={sortBy}
        onSortChange={setSortBy}
        chipsSlot={
          appliedCount > 0 ? (
            <FilterPills
              filterData={BrandFilters}
              setManualPriceRange={setManualPriceRange}
              clearFilters={clearFilters}
              filtersView={filtersView}
              setfiltersView={setfiltersView}
              setFilters={setFilters}
            />
          ) : null
        }
        chipCount={appliedCount}
        rail={
          <SideFilter
            setManualPriceRange={setManualPriceRange}
            manualPriceRange={manualPriceRange}
            filterData={BrandFilters}
            filtersView={filtersView}
            setfiltersView={setfiltersView}
            filters={filters}
            setFilters={setFilters}
          />
        }
        belowGrid={
          isBusy ? null : (
            <Pager
              page={currentPage}
              perPage={limit}
              total={pagination?.totalItems}
              onPageChange={handlePageChange}
            />
          )
        }
        emptyTitle="No brands match those filters"
        emptyBody="Try removing one, or clear them all to see every brand we carry."
        onClearAll={clearFilters}
        /* A brand row is identified by (type, id, vendor_id), not by `id`: a
           multi-brand store returns each vendor as its own row carrying the
           PARENT store's id, so id 844 comes back as both ADIDAS (vendor_id 116)
           and NIKE (118) on both endpoints this page uses. ⛔ Keying on `id`
           alone gave React two children with the same key, so one tile repeated
           down the grid while other brands disappeared. The href below composes
           the same three fields. */
        itemKey={(brand, i) => (
          brand?.id == null
            ? i
            : `${brand.type || 'brand'}-${brand.id}-${brand.vendor_id ?? ''}`
        )}
        renderItem={(brand) => (
          <BrandCard
            name={brand.name}
            logo={brand?.logo}
            color={brandColorOf(brand.name)}
            /* A real href, so the tile is a real link: keyboard reachable,
               announced and openable in a new tab. The router still intercepts
               the plain click. */
            href={`/brands/${brand.id}${brand.type === 'vendor' ? `/vendor/${brand.vendor_id}/` : '/'}`}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
              e.preventDefault();
              navigate(`/brands/${brand.id}${brand.type === 'vendor' ? `/vendor/${brand.vendor_id}/` : '/'}`);
            }}
          />
        )}
      />
    </div>
  );
};

export default Brands;